import { supabase } from "../supabase";
import {
  fetchClientTimelineBalanceSnapshot,
  updateClientBalance,
} from "./clientBalances";
import {
  SALE_PAYMENT_TERMS_SELECT_FIELDS,
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  normalizeSalePaymentTermsRow,
  setSalePaymentTermsColumnsSupport,
} from "./salesPaymentTermsSupport";
export {
  SALE_PAYMENT_TERMS_SELECT_FIELDS,
  getSalePaymentTermsColumnsSupport,
  isMissingSalePaymentTermsColumnsError,
  normalizeSalePaymentTermsRow,
  setSalePaymentTermsColumnsSupport,
} from "./salesPaymentTermsSupport";

const toDateOnly = (value) => String(value || "").slice(0, 10);
const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Las reglas puras de condicion de pago viven en ./paymentTerms.js: este
// modulo importa `../supabase`, asi que no se puede cargar fuera de Vite y
// cualquier consumidor que necesite solo las reglas terminaba copiandolas.
// Se re-exportan para no romper a los importadores existentes.
export {
  normalizePaymentConditionDays,
  getClientPaymentConditionDays,
  formatPaymentConditionLabel,
  hasClientCreditTerms,
  shouldDeferNonConsumerCollection,
  getSalePaymentConditionDays,
  getSalePaymentConditionLabel,
  shouldUseClientAccountForSale,
  getSaleReceivableDueDate,
  buildSalePaymentTerms,
} from "./paymentTerms";

export {
  isConsumidorFinalTaxCondition,
  isGenericConsumidorFinalClient,
} from "./consumidorFinal";

export function summarizeReceivables(receivables = [], referenceDate = new Date()) {
  const reference = new Date(`${toDateOnly(referenceDate.toISOString())}T12:00:00`);
  const summary = {
    totalPending: 0,
    totalExigible: 0,
    totalNotDue: 0,
    overdue1To30: 0,
    overdue31To60: 0,
    overdue61To90: 0,
    overdueOver90: 0,
    nextDueDate: null,
    openCount: 0,
  };

  for (const receivable of Array.isArray(receivables) ? receivables : []) {
    const pendingAmount = Math.max(0, Number(receivable?.pending_amount) || 0);
    if (pendingAmount <= 0) continue;

    summary.totalPending += pendingAmount;
    summary.openCount += 1;

    const dueDateValue = toDateOnly(receivable?.due_date);
    const dueDate = dueDateValue
      ? new Date(`${dueDateValue}T12:00:00`)
      : null;

    if (!summary.nextDueDate && dueDateValue) {
      summary.nextDueDate = dueDateValue;
    }

    if (!dueDate || Number.isNaN(dueDate.getTime())) {
      summary.totalExigible += pendingAmount;
      summary.overdue1To30 += pendingAmount;
      continue;
    }

    const dayDiff = Math.floor((reference.getTime() - dueDate.getTime()) / MS_PER_DAY);

    if (dayDiff <= 0) {
      summary.totalNotDue += pendingAmount;
      if (!summary.nextDueDate || dueDateValue < summary.nextDueDate) {
        summary.nextDueDate = dueDateValue;
      }
      continue;
    }

    summary.totalExigible += pendingAmount;

    if (dayDiff <= 30) summary.overdue1To30 += pendingAmount;
    else if (dayDiff <= 60) summary.overdue31To60 += pendingAmount;
    else if (dayDiff <= 90) summary.overdue61To90 += pendingAmount;
    else summary.overdueOver90 += pendingAmount;

    if (!summary.nextDueDate || dueDateValue < summary.nextDueDate) {
      summary.nextDueDate = dueDateValue;
    }
  }

  return summary;
}

function buildReversionMovementType(movementType) {
  if (movementType === "Venta POS") return "Reversion Venta POS";
  if (movementType === "Venta Online") return "Reversion Venta Online";
  return `Reversion ${movementType || "Venta"}`.trim();
}

const isClientMovementConflictError = (error) => {
  const normalizedCode = String(error?.code || "").trim();
  const normalizedMessage = String(error?.message || "").trim().toLowerCase();
  return (
    normalizedCode === "23505" ||
    Number(error?.status) === 409 ||
    normalizedMessage.includes("duplicate key")
  );
};

const CLIENT_MOVEMENT_SYNC_EPSILON = 0.000001;
const CLIENT_SALE_MOVEMENT_MISMATCH_MESSAGE =
  "La venta ya tiene movimientos de cuenta corriente asociados a otro cliente o con datos inconsistentes.";

const sumClientMovementImpact = (movements = []) =>
  (Array.isArray(movements) ? movements : []).reduce(
    (acc, movement) => acc + Number(movement?.amount || 0),
    0,
  );

const getUnexpectedSaleLinkedMovements = (movements = [], clientId) =>
  (Array.isArray(movements) ? movements : []).filter(
    (movement) => movement?.client_id !== clientId,
  );

export async function syncSaleClientDebit({
  saleId,
  organizationId,
  clientId,
  totalAmount,
  movementType,
  observations,
  shouldApply,
}) {
  if (!saleId || !organizationId || !clientId) {
    return { skipped: true, reason: "missing_context" };
  }

  const desiredImpact = shouldApply ? Math.max(0, Number(totalAmount) || 0) : 0;

  const { data: priorSaleMovements, error: movementsError } = await supabase
    .from("client_movements")
    .select("id, client_id, amount, type")
    .eq("organization_id", organizationId)
    .eq("sale_id", saleId);

  if (movementsError) throw movementsError;

  const unexpectedPriorMovements = getUnexpectedSaleLinkedMovements(
    priorSaleMovements,
    clientId,
  );
  if (unexpectedPriorMovements.length > 0) {
    throw new Error(CLIENT_SALE_MOVEMENT_MISMATCH_MESSAGE);
  }

  const priorMovements = (priorSaleMovements || []).filter(
    (movement) => movement?.client_id === clientId,
  );
  const currentImpact = sumClientMovementImpact(priorMovements);

  const diff = desiredImpact - currentImpact;
  if (Math.abs(diff) < CLIENT_MOVEMENT_SYNC_EPSILON) {
    return {
      skipped: true,
      reason: "already_synced",
      desiredImpact,
      currentImpact,
    };
  }

  const { data: client, error: clientError } = await fetchClientTimelineBalanceSnapshot({
    clientId,
    organizationId,
    ignoredSaleIds: [saleId],
    supabaseClient: supabase,
  });

  if (clientError) throw clientError;
  if (!client?.id) {
    return { skipped: true, reason: "client_not_found" };
  }

  const currentBalance = Number(client.current_balance ?? client.balance ?? 0);
  const nextBalance = currentBalance + diff;

  const { error: updateClientError } = await updateClientBalance({
    clientId: client.id,
    nextBalance,
    supabaseClient: supabase,
  });

  if (updateClientError) throw updateClientError;

  const appliedMovementType = diff > 0 ? movementType : buildReversionMovementType(movementType);
  const { error: insertMovementError } = await supabase
    .from("client_movements")
    .insert([
      {
        client_id: client.id,
        organization_id: organizationId,
        sale_id: saleId,
        type: appliedMovementType,
        amount: diff,
        current_balance: nextBalance,
        observations: observations || null,
      },
    ]);

  if (insertMovementError) {
    if (!isClientMovementConflictError(insertMovementError)) {
      throw insertMovementError;
    }

    const { data: reconciledSaleMovements, error: reconcileError } = await supabase
      .from("client_movements")
      .select("id, client_id, amount, type")
      .eq("organization_id", organizationId)
      .eq("sale_id", saleId);

    if (reconcileError) throw reconcileError;

    const unexpectedReconciledMovements = getUnexpectedSaleLinkedMovements(
      reconciledSaleMovements,
      client.id,
    );
    if (unexpectedReconciledMovements.length > 0) {
      throw new Error(CLIENT_SALE_MOVEMENT_MISMATCH_MESSAGE);
    }

    const reconciledMovements = (reconciledSaleMovements || []).filter(
      (movement) => movement?.client_id === client.id,
    );
    const reconciledImpact = sumClientMovementImpact(reconciledMovements);

    if (Math.abs(reconciledImpact - desiredImpact) < CLIENT_MOVEMENT_SYNC_EPSILON) {
      return {
        skipped: true,
        reason: "already_synced_after_conflict",
        desiredImpact,
        currentImpact: reconciledImpact,
      };
    }

    if (reconciledMovements.length === 1) {
      const { error: updateMovementError } = await supabase
        .from("client_movements")
        .update({
          type: appliedMovementType,
          amount: desiredImpact,
          current_balance: nextBalance,
          observations: observations || null,
        })
        .eq("id", reconciledMovements[0].id);

      if (updateMovementError) throw updateMovementError;

      return {
        applied: true,
        diff,
        movementType: appliedMovementType,
        nextBalance,
        mode: "updated_after_conflict",
      };
    }

    throw insertMovementError;
  }

  return {
    applied: true,
    diff,
    movementType: appliedMovementType,
    nextBalance,
  };
}

export async function syncSaleReceivable({
  saleId,
  organizationId,
  clientId,
  siteId,
  source,
  description,
  dueDate,
  totalAmount,
  active,
}) {
  if (!saleId || !organizationId || !clientId) {
    return { skipped: true, reason: "missing_context" };
  }

  const normalizedTotal = Math.max(0, Number(totalAmount) || 0);
  const shouldBeActive = Boolean(active) && normalizedTotal > 0;
  const normalizedDueDate = String(dueDate || new Date().toISOString().slice(0, 10)).slice(0, 10);

  const { data: existing, error: existingError } = await supabase
    .from("sales_receivables")
    .select("id, pending_amount, settled_amount")
    .eq("sale_id", saleId)
    .maybeSingle();

  if (existingError) throw existingError;

  if (!existing?.id) {
    if (!shouldBeActive) return { skipped: true, reason: "inactive_without_existing" };

    const payload = {
      organization_id: organizationId,
      sale_id: saleId,
      client_id: clientId,
      site_id: siteId || null,
      source: source || "pos",
      description: description || null,
      due_date: normalizedDueDate,
      total_amount: normalizedTotal,
      pending_amount: normalizedTotal,
      settled_amount: 0,
      is_active: true,
    };

    const { error: insertError } = await supabase.from("sales_receivables").insert([payload]);
    if (insertError) throw insertError;

    return { applied: true, created: true };
  }

  const settledAmount = Math.max(0, Number(existing.settled_amount) || 0);
  const nextPendingAmount = shouldBeActive ? Math.max(0, normalizedTotal - settledAmount) : 0;

  const { error: updateError } = await supabase
    .from("sales_receivables")
    .update({
      client_id: clientId,
      site_id: siteId || null,
      source: source || "pos",
      description: description || null,
      due_date: normalizedDueDate,
      total_amount: normalizedTotal,
      pending_amount: nextPendingAmount,
      is_active: shouldBeActive && nextPendingAmount > 0,
      updated_at: new Date().toISOString(),
    })
    .eq("id", existing.id);

  if (updateError) throw updateError;

  return { applied: true, updated: true };
}

export async function deactivateSaleReceivable(saleId) {
  if (!saleId) return { skipped: true, reason: "missing_sale_id" };

  const { error } = await supabase
    .from("sales_receivables")
    .update({
      is_active: false,
      updated_at: new Date().toISOString(),
    })
    .eq("sale_id", saleId);

  if (error) throw error;
  return { applied: true };
}

export async function applyCollectionToReceivables({
  organizationId,
  clientId,
  amount,
  financialMovementId,
}) {
  const normalizedAmount = Math.max(0, Number(amount) || 0);
  if (!organizationId || !clientId || !financialMovementId || normalizedAmount <= 0) {
    return { skipped: true, reason: "missing_context" };
  }

  let remainingAmount = normalizedAmount;

  const { data: receivables, error } = await supabase
    .from("sales_receivables")
    .select("id, pending_amount, settled_amount, due_date, created_at")
    .eq("organization_id", organizationId)
    .eq("client_id", clientId)
    .eq("is_active", true)
    .gt("pending_amount", 0)
    .order("due_date", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) throw error;

  for (const receivable of receivables || []) {
    if (remainingAmount <= 0) break;

    const pendingAmount = Math.max(0, Number(receivable.pending_amount) || 0);
    if (pendingAmount <= 0) continue;

    const appliedAmount = Math.min(remainingAmount, pendingAmount);
    const nextPendingAmount = pendingAmount - appliedAmount;
    const nextSettledAmount = Math.max(0, Number(receivable.settled_amount) || 0) + appliedAmount;

    const { error: updateError } = await supabase
      .from("sales_receivables")
      .update({
        pending_amount: nextPendingAmount,
        settled_amount: nextSettledAmount,
        is_active: nextPendingAmount > 0,
        updated_at: new Date().toISOString(),
      })
      .eq("id", receivable.id);

    if (updateError) throw updateError;

    const { error: applicationError } = await supabase
      .from("sales_receivable_applications")
      .insert([
        {
          receivable_id: receivable.id,
          financial_movement_id: financialMovementId,
          amount: appliedAmount,
        },
      ]);

    if (applicationError) throw applicationError;

    remainingAmount -= appliedAmount;
  }

  return {
    applied: true,
    appliedAmount: normalizedAmount - remainingAmount,
    remainingAmount,
  };
}

export async function fetchOpenClientReceivables({
  organizationId,
  clientId,
}) {
  if (!organizationId || !clientId) {
    return { data: [], error: null };
  }

  const { data: receivables, error } = await supabase
    .from("sales_receivables")
    .select(
      "id, sale_id, due_date, pending_amount, settled_amount, total_amount, description, created_at",
    )
    .eq("organization_id", organizationId)
    .eq("client_id", clientId)
    .eq("is_active", true)
    .gt("pending_amount", 0)
    .order("due_date", { ascending: true })
    .order("created_at", { ascending: true });

  if (error) {
    return { data: [], error };
  }

  const saleIds = Array.from(
    new Set((receivables || []).map((row) => row.sale_id).filter(Boolean)),
  );
  const salesById = new Map();

  if (saleIds.length > 0) {
    const selectBase =
      "id, created_at, numero_factura, total";
    const selectWithTerms = `${selectBase}, ${SALE_PAYMENT_TERMS_SELECT_FIELDS}`;

    const buildSalesQuery = (selectClause) =>
      supabase
        .from("sales")
        .select(selectClause)
        .eq("organization_id", organizationId)
        .in("id", saleIds);

    let salesResponse;
    const support = getSalePaymentTermsColumnsSupport();

    if (support !== false) {
      salesResponse = await buildSalesQuery(selectWithTerms);

      if (!salesResponse.error) {
        setSalePaymentTermsColumnsSupport(true);
      } else if (isMissingSalePaymentTermsColumnsError(salesResponse.error)) {
        setSalePaymentTermsColumnsSupport(false);
        salesResponse = await buildSalesQuery(selectBase);
      }
    } else {
      salesResponse = await buildSalesQuery(selectBase);
    }

    if (salesResponse?.error) {
      return { data: [], error: salesResponse.error };
    }

    (salesResponse.data || []).forEach((sale) => {
      const normalizedSale = normalizeSalePaymentTermsRow(sale);
      salesById.set(normalizedSale.id, normalizedSale);
    });
  }

  return {
    data: (receivables || []).map((receivable) => {
      const sale = salesById.get(receivable.sale_id) || null;
      return {
        ...receivable,
        sale_number: sale?.numero_factura || null,
        sale_created_at: sale?.created_at || receivable.created_at || null,
        sale_total: Number(sale?.total || receivable.total_amount || 0),
        payment_condition_label:
          sale?.payment_condition_label || null,
        payment_due_date:
          sale?.payment_due_date || receivable.due_date || null,
      };
    }),
    error: null,
  };
}

export async function applyCollectionAllocations({
  financialMovementId,
  allocations = [],
}) {
  if (!financialMovementId) {
    return { skipped: true, reason: "missing_financial_movement_id" };
  }

  const normalizedAllocations = (Array.isArray(allocations) ? allocations : [])
    .map((allocation) => ({
      receivable_id: allocation?.receivable_id || allocation?.id || null,
      amount: Math.max(0, Number(allocation?.amount || allocation?.allocation) || 0),
    }))
    .filter((allocation) => allocation.receivable_id && allocation.amount > 0);

  if (normalizedAllocations.length === 0) {
    return { skipped: true, reason: "no_allocations" };
  }

  const receivableIds = normalizedAllocations.map((allocation) => allocation.receivable_id);
  const { data: receivables, error } = await supabase
    .from("sales_receivables")
    .select("id, pending_amount, settled_amount")
    .in("id", receivableIds);

  if (error) throw error;

  const receivablesById = new Map(
    (receivables || []).map((receivable) => [receivable.id, receivable]),
  );

  for (const allocation of normalizedAllocations) {
    const receivable = receivablesById.get(allocation.receivable_id);
    if (!receivable?.id) continue;

    const pendingAmount = Math.max(0, Number(receivable.pending_amount) || 0);
    const appliedAmount = Math.min(allocation.amount, pendingAmount);
    if (appliedAmount <= 0) continue;

    const nextPendingAmount = pendingAmount - appliedAmount;
    const nextSettledAmount =
      Math.max(0, Number(receivable.settled_amount) || 0) + appliedAmount;

    const { error: updateError } = await supabase
      .from("sales_receivables")
      .update({
        pending_amount: nextPendingAmount,
        settled_amount: nextSettledAmount,
        is_active: nextPendingAmount > 0,
        updated_at: new Date().toISOString(),
      })
      .eq("id", receivable.id);

    if (updateError) throw updateError;

    const { error: applicationError } = await supabase
      .from("sales_receivable_applications")
      .insert([
        {
          receivable_id: receivable.id,
          financial_movement_id: financialMovementId,
          amount: appliedAmount,
        },
      ]);

    if (applicationError) throw applicationError;
  }

  return { applied: true, count: normalizedAllocations.length };
}

export async function revertCollectionReceivableApplications(financialMovementId) {
  if (!financialMovementId) {
    return { skipped: true, reason: "missing_financial_movement_id" };
  }

  const { data: applications, error } = await supabase
    .from("sales_receivable_applications")
    .select("id, receivable_id, amount")
    .eq("financial_movement_id", financialMovementId);

  if (error) throw error;
  if (!applications?.length) {
    return { skipped: true, reason: "no_applications" };
  }

  for (const application of applications) {
    const { data: receivable, error: receivableError } = await supabase
      .from("sales_receivables")
      .select("id, pending_amount, settled_amount")
      .eq("id", application.receivable_id)
      .maybeSingle();

    if (receivableError) throw receivableError;
    if (!receivable?.id) continue;

    const appliedAmount = Math.max(0, Number(application.amount) || 0);
    const nextPendingAmount = Math.max(0, Number(receivable.pending_amount) || 0) + appliedAmount;
    const nextSettledAmount = Math.max(0, (Number(receivable.settled_amount) || 0) - appliedAmount);

    const { error: updateError } = await supabase
      .from("sales_receivables")
      .update({
        pending_amount: nextPendingAmount,
        settled_amount: nextSettledAmount,
        is_active: nextPendingAmount > 0,
        updated_at: new Date().toISOString(),
      })
      .eq("id", receivable.id);

    if (updateError) throw updateError;
  }

  const { error: deleteError } = await supabase
    .from("sales_receivable_applications")
    .delete()
    .eq("financial_movement_id", financialMovementId);

  if (deleteError) throw deleteError;

  return { applied: true, count: applications.length };
}
